import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { usePageMeta, useJsonLd, breadcrumbJsonLd } from "../lib/seo";
import { ErrorBanner, LoadingState } from "../components/States";
import { formatDateTime } from "../lib/format";

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: post, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.post(slug ?? ""),
    queryFn: ({ signal }) => apiClient.getPost(slug as string, signal),
    enabled: Boolean(slug),
  });

  usePageMeta({
    title: post?.title ?? "Blog",
    description: post?.excerpt ?? "An update from MarketOnBase.",
    path: `/blog/${slug ?? ""}`,
  });

  useJsonLd(
    useMemo(
      () =>
        post
          ? breadcrumbJsonLd([
              { name: "Home", path: "/" },
              { name: "Blog", path: "/blog" },
              { name: post.title, path: `/blog/${post.slug}` },
            ])
          : null,
      [post],
    ),
  );

  if (isLoading) return <LoadingState label="Loading post…" />;
  if (isError) return <ErrorBanner error={error} onRetry={() => void refetch()} />;
  if (!post) return null;

  return (
    <article className="stack" style={{ maxWidth: 720 }}>
      <div>
        <p className="muted text-sm">{post.publishedAt ? formatDateTime(post.publishedAt) : ""}</p>
        <h1>{post.title}</h1>
      </div>
      {/* Trusted content: bodyHtml is only ever written by the single admin account (see
          services/posts.ts's docblock) — same trust boundary every other admin-only mutation in
          this codebase relies on, not user-generated content. */}
      <div className="prose" dangerouslySetInnerHTML={{ __html: post.bodyHtml }} />
    </article>
  );
}
